import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { OfficeAgentCode, OfficeAgentDto, OfficeCountsDto } from '../../../../shared/types';
import { AGENT_COLOR, modeInfo, stateLabel, roleText } from './OfficeScene';
import { Tip } from './Tip';

/**
 * Vista «Mappa» dell'Ufficio: tutta la squadra in una schermata, dal vivo.
 * Marco e Luca stanno a sinistra; gli agenti occupano centro e destra.
 * Le frecce compaiono solo quando qualcosa passa davvero fra due persone o agenti.
 */

/** Chiamata di prova in corso (o appena finita): le frasi dette, in ordine */
export interface MapCall {
  active: boolean;
  lines: { who: 'tu' | 'agente'; text: string }[];
}

export type MapBeat = OfficeAgentCode | 'me';
export type MapMember = MapBeat | 'luca';
export interface MapTransfer { id: number; from: MapMember; to: MapMember }
/** Cose aperte di un assistente che aspettano te: quante e quali (per il suggerimento) */
export interface MapWaiting {
  n: number;
  text: string;
}

interface Pt {
  x: number;
  y: number;
}

interface Link {
  key: string;
  from: Pt;
  to: Pt;
  tone: 'hot' | 'bad' | 'off' | '';
}

interface Layout {
  me: Pt;
  luca: Pt;
  agents: Pt[];
  /** Scala dei pallini (1 = schermo del computer) */
  k: number;
  /** Larghezza di un pallino con la sua scritta */
  lw: number;
  /** Telefono: pallini in colonna con la scritta di fianco */
  portrait: boolean;
}

const rad = (deg: number) => (deg * Math.PI) / 180;

/**
 * Dove stanno i pallini.
 * Computer: Marco e Luca a sinistra, gli agenti distribuiti tra centro e destra.
 * Telefono (o finestra stretta): tutti in colonna, con le scritte di fianco.
 */
export function mapLayout(w: number, h: number, n: number): Layout {
  const portrait = w < 900;
  if (!portrait) {
    const k = Math.max(0.8, Math.min(1.1, Math.min(w / 1100, h / 560)));
    const lw = 210;
    const humanX = Math.max(lw / 2 + 18, w * 0.12);
    const me = { x: humanX, y: h * 0.32 };
    const luca = { x: humanX, y: h * 0.68 };
    const agents = n === 3
      ? [{ x: w * 0.43, y: h * 0.22 }, { x: w * 0.77, y: Math.min(h * 0.35, h - 350) }, { x: w * 0.59, y: Math.min(h * 0.68, h - 190) }]
      : Array.from({ length: n }, (_, i) => {
        const a = rad(-135 + (360 * i) / Math.max(1, n));
        return { x: w * (0.62 + 0.25 * Math.cos(a)), y: h * (0.47 + 0.29 * Math.sin(a)) };
      });
    return { me, luca, agents, k, lw, portrait };
  }
  const k = Math.max(0.74, Math.min(0.92, w / 420));
  const x = Math.round(Math.max(84 * k, Math.min(w * 0.22, 100)));
  const step = Math.max(155, Math.min(175, (h - 150) / (n + 1)));
  // la colonna sta in mezzo, in alto c'è posto per «Dal vivo»
  const top = Math.max(75, (h - step * (n + 1)) / 2);
  const me = { x, y: top };
  const luca = { x, y: top + step };
  const agents = Array.from({ length: n }, (_, i) => ({ x, y: top + step * (i + 2) }));
  return { me, luca, agents, k, lw: Math.min(620, w - x + 26 * k - 12), portrait };
}

const hhmm = (iso: string) => new Date(iso).toLocaleTimeString('it-IT', { hour: '2-digit', minute: '2-digit' });

function useSize<T extends HTMLElement>(): [React.RefObject<T | null>, { w: number; h: number }] {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setSize((s) => (s.w === el.clientWidth && s.h === el.clientHeight ? s : { w: el.clientWidth, h: el.clientHeight }));
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

/** Ogni agente mostra come sta: impegnato, preoccupato, tranquillo o spento. */
function AgentFace({ mood }: { mood: 'work' | 'problem' | 'idle' | 'off' }) {
  return (
    <svg className={`of-mp-agent-face ${mood}`} viewBox="0 0 100 100" aria-hidden="true">
      {mood === 'work' ? (
        <>
          <path className="brow" d="M25 33 Q34 30 42 37 M58 37 Q66 30 75 33" />
          <path className="eyes" d="M29 51 Q36 47 43 50 M57 50 Q64 47 71 51" />
          <path className="mouth" d="M40 70 H60" />
        </>
      ) : mood === 'problem' ? (
        <>
          <rect className="eyes" x="30" y="37" width="11" height="21" rx="5.5" />
          <rect className="eyes" x="59" y="37" width="11" height="21" rx="5.5" />
          <path className="mouth" d="M39 73 Q50 62 61 73" />
        </>
      ) : mood === 'off' ? (
        <>
          <path className="eyes" d="M28 49 Q35 54 42 49 M58 49 Q65 54 72 49" />
          <path className="mouth" d="M44 70 H56" />
        </>
      ) : (
        <>
          <rect className="eyes" x="31" y="40" width="9" height="15" rx="4.5" />
          <rect className="eyes" x="60" y="40" width="9" height="15" rx="4.5" />
          <path className="mouth" d="M42 69 Q50 75 58 69" />
        </>
      )}
    </svg>
  );
}

interface Props {
  agents: OfficeAgentDto[];
  counts: OfficeCountsDto;
  meName: string;
  waiting: Partial<Record<OfficeAgentCode, MapWaiting>>;
  beats: Record<MapBeat, number>;
  transfers: MapTransfer[];
  call: MapCall | null;
  live: boolean;
  onPick: (code: OfficeAgentCode) => void;
  onMe: () => void;
  onHub: () => void;
}

export function OfficeMap({ agents, counts, meName, waiting, beats, transfers, call, live, onPick, onMe, onHub }: Props) {
  const [ref, { w, h }] = useSize<HTMLDivElement>();
  const L = useMemo(() => (w > 0 && h > 0 ? mapLayout(w, h, agents.length) : null), [w, h, agents.length]);

  const forMe = counts.approve + counts.alerts;
  const meItems = [
    forMe ? `${forMe} da approvare o da vedere` : '',
    counts.callback ? `${counts.callback} da richiamare` : '',
    counts.bandi ? `${counts.bandi} ${counts.bandi === 1 ? 'bando' : 'bandi'}` : '',
    counts.toCheck ? `${counts.toCheck} da controllare` : '',
  ].filter(Boolean);

  const sz = (base: number) => `${Math.round(base * (L?.k ?? 1))}px`;
  // Larghezza di pallino + scritta: sul telefono arriva fino al bordo destro
  const lwOf = (base: number, extra = 0) => `${Math.round(L?.portrait ? Math.min(L.lw, w - L.me.x + (base * L.k) / 2 - 12) : (L?.lw ?? 210) + extra)}px`;

  const links: Link[] = [];
  if (L) {
    const connect = (key: string, from: Pt, to: Pt, first: number, second?: number) => {
      const related = [agents[first], second === undefined ? undefined : agents[second]].filter((a): a is OfficeAgentDto => !!a);
      const off = related.every((a) => a.mode === 'spento');
      const problem = related.some((a) => a.mode !== 'spento' && a.state === 'errore');
      links.push({ key, from, to, tone: off ? 'off' : problem ? 'bad' : '' });
    };
    const segreteria = agents.findIndex((agent) => agent.code === 'segreteria');
    if (segreteria >= 0) {
      connect('marco-segreteria', L.me, L.agents[segreteria], segreteria);
      for (const code of ['tecnico', 'opportunita'] as const) {
        const to = agents.findIndex((agent) => agent.code === code);
        if (to >= 0) connect(`segreteria-${code}`, L.agents[segreteria], L.agents[to], segreteria, to);
      }
    }
  }

  const pathOf = ({ key, from, to }: Link) => {
    if (L?.portrait && (key === 'marco-segreteria' || key === 'segreteria-opportunita')) {
      const side = Math.max(24, Math.min(from.x, to.x) - 70);
      return `M ${from.x} ${from.y} C ${side} ${from.y} ${side} ${to.y} ${to.x} ${to.y}`;
    }
    return `M ${from.x} ${from.y} L ${to.x} ${to.y}`;
  };
  const pointOf = (member: MapMember): Pt | undefined => member === 'me' ? L?.me : member === 'luca' ? L?.luca : L?.agents[agents.findIndex((a) => a.code === member)];
  const transferPath = (from: Pt, to: Pt) => {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const distance = Math.hypot(dx, dy) || 1;
    const start = { x: from.x + (dx / distance) * 32, y: from.y + (dy / distance) * 32 };
    const end = { x: to.x - (dx / distance) * 35, y: to.y - (dy / distance) * 35 };
    const curve = L?.portrait ? 0 : Math.min(80, distance * 0.12);
    return `M ${start.x} ${start.y} Q ${(start.x + end.x) / 2} ${(start.y + end.y) / 2 - curve} ${end.x} ${end.y}`;
  };

  return (
    <div ref={ref} className={`of-map${L?.portrait ? ' portrait' : ''}`} style={L?.portrait ? { minHeight: Math.max(560, (agents.length + 2) * 165) } : undefined}>
      {L && (
        <>
          <svg className="of-mp-links" viewBox={`0 0 ${w} ${h}`} aria-hidden="true">
            <defs>
              <marker id="of-mp-arrow-agent" markerWidth="10" markerHeight="10" refX="8" refY="5" orient="auto" markerUnits="userSpaceOnUse"><path d="M1 1 L9 5 L1 9 Z" fill="var(--good)" /></marker>
              <marker id="of-mp-arrow-human" markerWidth="10" markerHeight="10" refX="8" refY="5" orient="auto" markerUnits="userSpaceOnUse"><path d="M1 1 L9 5 L1 9 Z" fill="var(--warn)" /></marker>
            </defs>
            {links.map((link) => (
              <g key={link.key}>
                <path className={`of-mp-line ${link.tone}`} d={pathOf(link)} />
              </g>
            ))}
            {transfers.map((transfer) => {
              const from = pointOf(transfer.from);
              const to = pointOf(transfer.to);
              if (!from || !to) return null;
              const human = transfer.from === 'me' || transfer.from === 'luca';
              return <path key={transfer.id} className={`of-mp-transfer${human ? ' human' : ''}`} d={transferPath(from, to)} markerEnd={`url(#of-mp-arrow-${human ? 'human' : 'agent'})`} />;
            })}
          </svg>
          {/* Tu */}
          <button
            type="button"
            className="of-mp-node me"
            style={{ left: L.me.x, top: L.me.y, '--sz': sz(62), '--lw': lwOf(62), '--dl': '-2.5s' } as React.CSSProperties}
            title={`${forMe ? `${forMe} ${forMe === 1 ? 'cosa' : 'cose'} per te` : 'Tutto a posto'}${meItems.map((x) => `
• ${x}`).join('')}
→ Clicca per aprire «Da approvare»`}
            aria-label={`${meName}: ${forMe ? `${forMe} cose per te` : 'tutto a posto'}. Apri Da approvare`}
            onClick={onMe}
          >
            <span className="of-mp-ball">
              <span className="of-mp-ring" />
              <span className="of-mp-person-photo"><img src="marco-ufficio.png" alt="" /></span>
              {beats.me > 0 && <span key={beats.me} className="of-mp-burst late" />}
              {forMe > 0 && <span className="of-mp-badge">{forMe > 99 ? '99+' : forMe}</span>}
            </span>
            <span className="of-mp-label">
              <span className={`of-mp-now${forMe ? ' warn' : ''}`}>{forMe ? `${forMe} ${forMe === 1 ? 'cosa' : 'cose'} per te` : 'Tutto a posto'}</span>
            </span>
          </button>

          {/* Luca è una persona della squadra, non un agente automatico. */}
          <div
            className="of-mp-node luca"
            style={{ left: L.luca.x, top: L.luca.y, '--sz': sz(58), '--lw': lwOf(58) } as React.CSSProperties}
            aria-label="Luca Errico"
          >
            <span className="of-mp-ball">
              <span className="of-mp-ring" />
              <span className="of-mp-person-photo"><img src="luca-ufficio.png" alt="" /></span>
            </span>
            <span className="of-mp-label"><span className="of-mp-person-caption">Luca</span></span>
          </div>

          {/* Gli assistenti */}
          {agents.map((a, i) => {
            const p = L.agents[i];
            const off = a.mode === 'spento';
            const st = off ? 'off' : a.state === 'fermo' ? 'attesa' : a.state;
            const wt = waiting[a.code];
            // La Segreteria risponde al telefono: la scheda mostra la conversazione dal vivo
            const calling = a.code === 'segreteria' && !off && !!call;
            const pct = a.progress ? Math.round((a.progress.done / Math.max(1, a.progress.total)) * 100) : 0;
            const status = off ? 'Non lavora' : a.state === 'lavora' ? 'Sta lavorando' : a.state === 'errore' ? 'Problema' : 'Fermo ora';
            const detail = off ? '' : a.state === 'errore'
              ? [a.activity.replace(/^Ultimo controllo: non funziona: /, 'Sito: '), a.error].filter(Boolean).join(' · ')
              : a.activity;
            const next = !off && a.state !== 'lavora' && a.state !== 'errore' && a.nextRunAt ? `Riprende alle ${hhmm(a.nextRunAt)}` : '';
            return (
              <button
                key={a.code}
                type="button"
                className={`of-mp-node agent ${calling ? 'lavora calling' : st}`}
                style={{ left: p.x, top: p.y, '--sz': sz(52), '--lw': lwOf(52, 78), '--ac': AGENT_COLOR[a.code], '--dl': `${-i * 1.7}s` } as React.CSSProperties}
                title={`${a.name}
${roleText(a)}${wt?.n ? `
Per te:${wt.text.split(', ').map((x: string) => `
• ${x}`).join('')}` : ''}
→ Clicca per vedere cosa fa`}
                aria-label={`${a.name}: ${stateLabel(a)}. ${a.activity}`}
                onClick={() => onPick(a.code)}
              >
                <span className="of-mp-ball">
                  <span className="of-mp-ring" />
                  <span className="of-mp-face"><AgentFace mood={off ? 'off' : calling ? 'work' : a.state === 'errore' ? 'problem' : a.state === 'lavora' ? 'work' : 'idle'} /></span>
                  {beats[a.code] > 0 && <span key={beats[a.code]} className="of-mp-burst" />}
                  {!!wt?.n && <span className="of-mp-badge">{wt.n > 99 ? '99+' : wt.n}</span>}
                </span>
                <span className="of-mp-label of-mp-agent-card">
                  <span className="of-mp-name">
                    {a.name}
                    <span className="of-mp-kind">Agente</span>
                  </span>
                  <span className={`of-mp-status ${calling ? 'lavora' : st}`}><i />{calling ? (call!.active ? 'Al telefono' : 'Chiamata finita') : status}<Tip content={modeInfo(a).content} className={`of-mp-tag ${a.mode}`}>{modeInfo(a).label}</Tip></span>
                  {calling ? (
                    <span className="of-mp-call">
                      {call!.lines.slice(-6).map((l, k) => (
                        <span key={k} className={`of-mp-say ${l.who}`}>
                          <b>{l.who === 'tu' ? meName : 'Segreteria'}</b> {l.text}
                        </span>
                      ))}
                      {call!.active && <span className="of-mp-say dots" aria-hidden="true">…</span>}
                    </span>
                  ) : detail && <span className="of-mp-activity" title={detail}>{detail}</span>}
                  {!calling && next && <span className="of-mp-next">{next}</span>}
                  {a.progress && (
                    <span className="of-mp-progress">
                      <span className="of-mp-bar"><i style={{ width: `${pct}%` }} /></span>
                      <small>{a.progress.done}/{a.progress.total}</small>
                    </span>
                  )}
                </span>
              </button>
            );
          })}

          <div className={`of-mp-live${live ? ' on' : ''}`} title={live ? 'Quello che vedi si aggiorna da solo, appena succede' : 'Il collegamento si è interrotto: riprovo da solo'}>
            <i />
            {live ? 'Dal vivo' : 'Non collegato: riprovo…'}
          </div>
          <button type="button" className="of-mp-diary" onClick={onHub} title="Tutto quello che gli agenti hanno letto, deciso e fatto, con il perché: lì correggi gli errori">Cosa hanno fatto</button>
          <div className="of-mp-foot">
            <span className="of-mp-legend" aria-hidden="true">
              <span>
                <i className="good" /> Al lavoro
              </span>
              <span>
                <i className="info" /> In attesa
              </span>
              <span>
                <i className="bad" /> Problema
              </span>
              <span>
                <i className="off" /> Spento
              </span>
            </span>
          </div>
        </>
      )}
    </div>
  );
}
